import React, { useState, useContext, useEffect } from "react";
import "../styles/ProfilePage.scss";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../contexts/AuthContext";
import axiosInstance from "../utils/axios";
import { toast, ToastContainer } from "react-toastify";
import { dateFormat } from "../utils/dateFormats";

const ProfilePage = () => {
  const { logout, setName } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState("profile");
  const navigate = useNavigate();

  const [profile, setProfile] = useState({});
  const [bookingHistory, setBookingHistory] = useState([]);
  const [eventBookingHistory, setEventBookingHistory] = useState([]);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subLoading, setSubLoading] = useState(false);

  useEffect(() => {
    fetchProfile();
    fetchBookingHistory();
    fetchEventBookingHistory();
    fetchSubscriptionStatus();
  }, []);

  /* ========================= FETCH FUNCTIONS ========================= */

  const extractArray = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.results)) return data.results;
    return [];
  };

  const fetchBookingHistory = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("customer/booking-history");
      setBookingHistory(extractArray(res.data));
    } catch (err) {
      toast.error("Cannot fetch movie booking history");
    } finally {
      setLoading(false);
    }
  };

  const fetchEventBookingHistory = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(
        "/news_and_events/my-event-tickets/"
      );
      setEventBookingHistory(extractArray(res.data));
    } catch (err) {
      toast.error("Cannot fetch event booking history");
    } finally {
      setLoading(false);
    }
  };

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("customer/profile");

      const cleanedData = {
        ...res.data,
        mobile:
          typeof res.data.mobile === "string" &&
            res.data.mobile.toLowerCase().startsWith("tmp")
            ? ""
            : res.data.mobile,
      };

      setProfile(cleanedData);
    } catch (err) {
      toast.error("Cannot fetch profile");
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const res = await axiosInstance.get(
        "/subscription/subscription/is_subscribed/"
      );
      setIsSubscribed(res.data?.subscribed || false);
    } catch (err) {
      console.error("Subscription status check failed");
    }
  };

  /* ========================= INPUT HANDLING ========================= */

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (value?.toLowerCase().startsWith("tmp")) return;

    setProfile((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!profile.name?.trim())
      newErrors.name = "First name is required";

    if (!profile.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(profile.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (!profile.mobile?.trim()) {
      newErrors.mobile = "Phone number is required";
    }

    if (!profile.dob) {
      newErrors.dob = "Date of birth is required";
    }

    return newErrors;
  };

  /* ========================= SUBMIT ========================= */

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);

    try {
      await axiosInstance.put("customer/profile-update", profile);
      setName(profile.name);
      toast.success("Profile updated successfully");
    } catch (err) {
      toast.error("Cannot update profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ========================= ACCOUNT ========================= */

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleDeleteClick = async () => {
    if (
      window.confirm(
        "Are you sure you want to delete your account? This cannot be undone."
      )
    ) {
      try {
        await axiosInstance.post("customer/account-deletion");
        toast.success("Account deleted successfully.");
        logout();
        navigate("/");
      } catch {
        toast.error("Failed to delete account.");
      }
    }
  };

  /* ========================= SUBSCRIPTION ========================= */

  const handleSubscriptionToggle = async () => {
    if (subLoading) return;
    setSubLoading(true);

    try {
      if (isSubscribed) {
        await axiosInstance.post(
          "/subscription/subscription/un_subscribe/"
        );
        toast.success("Unsubscribed successfully");
        setIsSubscribed(false);
      } else {
        await axiosInstance.post(
          "/subscription/subscription/subscribe/",
          { email: profile.email }
        );
        toast.success("Subscribed successfully");
        setIsSubscribed(true);
      }
    } catch {
      toast.error("Subscription action failed");
    } finally {
      setSubLoading(false);
    }
  };

  /* ========================= RENDER ========================= */

  return (
    <div className="profile-page">
      <div className="profile-header">

        <div className="profile-info">
          <h2>{profile.name}</h2>
          <p>{profile.email}</p>
          {profile.created_at && (
            <p>📅 Member since {dateFormat(profile.created_at)}</p>
          )}

          <button onClick={handleLogout}>Logout</button>
          <button onClick={handleDeleteClick}>Delete Account</button>
          <button
            onClick={handleSubscriptionToggle}
            disabled={subLoading}
          >
            {subLoading
              ? "Processing..."
              : isSubscribed
                ? "Unsubscribe"
                : "Subscribe"}
          </button>
        </div>
      </div>

      <div className="tabs">
        <button
          className={activeTab === "profile" ? "active" : ""}
          onClick={() => setActiveTab("profile")}
        >
          👤 Profile
        </button>

        <button
          className={activeTab === "movie-history" ? "active" : ""}
          onClick={() => setActiveTab("movie-history")}
        >
          🎟 Movie Booking History
        </button>

        <button
          className={activeTab === "event-history" ? "active" : ""}
          onClick={() => setActiveTab("event-history")}
        >
          🎟 Event Booking History
        </button>
      </div>

      {/* ================= PROFILE TAB ================= */}

      {activeTab === "profile" && (
        <div className="tab-content">
          <h4>Welcome, {profile.name}!</h4>

          <div className="form-floating">
            <input
              type="text"
              className={`form-control ${errors.name ? "is-invalid" : ""}`}
              name="name"
              value={profile.name || ""}
              onChange={handleInputChange}
              placeholder="First Name"
            />
            <label>First Name</label>
            {errors.name && (
              <span className="error-message">{errors.name}</span>
            )}
          </div>

          <br />

          <div className="form-floating">
            <input
              type="email"
              className="form-control"
              name="email"
              value={profile.email || ""}
              disabled
            />
            <label>Email</label>
          </div>

          <br />

          <div className="form-floating">
            <input
              type="tel"
              className={`form-control ${errors.mobile ? "is-invalid" : ""}`}
              name="mobile"
              value={profile.mobile || ""}
              onChange={handleInputChange}
            />
            <label>Phone Number</label>
            {errors.mobile && (
              <span className="error-message">{errors.mobile}</span>
            )}
          </div>

          <br />

          <div className="form-floating">
            <input
              type="date"
              className={`form-control ${errors.dob ? "is-invalid" : ""}`}
              name="dob"
              value={profile.dob || ""}
              onChange={handleInputChange}
            />
            <label>Date of Birth</label>
            {errors.dob && (
              <span className="error-message">{errors.dob}</span>
            )}
          </div>

          <br />

          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="btn btn-primary"
          >
            {loading ? "Updating..." : "Update"}
          </button>
        </div>
      )}

      {/* ========================= MOVIE HISTORY ========================= */}

      {activeTab === "movie-history" && (
        <div className="tab-content">
          <h3>Movie Booking History</h3>

          {loading ? (
            <p>Loading...</p>
          ) : bookingHistory.length > 0 ? (
            <div className="responsive-table">
              <table className="history-table">
                <thead>
                  <tr>
                    <th>Movie</th>
                    <th>Theatre</th>
                    <th>Date</th>
                    <th>Seats</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th>QR</th>
                  </tr>
                </thead>
                <tbody>
                  {bookingHistory.map((booking, idx) => (
                    <tr key={idx}>
                      <td>{booking.movie_name}</td>
                      <td>{booking.theatre}</td>
                      <td>{booking.show_date}</td>
                      <td>
                        {booking.seats?.length
                          ? booking.seats
                            .map(
                              (seat) =>
                                `${seat.section}-${seat.block}-${seat.row}-${seat.number}`
                            )
                            .join(", ")
                          : "-"}
                      </td>
                      <td>{booking.amount_to_pay || 0}</td>
                      <td>
                        <span
                          className={
                            booking.paid ? "status-badge paid" : "status-badge unpaid"
                          }
                        >
                          {booking.paid ? "Paid" : "Unpaid"}
                        </span>
                      </td>
                      <td>
                        {booking.qr_code && (
                          <img
                            src={booking.qr_code}
                            alt="QR"
                            className="qr-small"
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>No movie booking history found.</p>
          )}
        </div>
      )}


      {/* ========================= EVENT HISTORY ========================= */}

      {activeTab === "event-history" && (
        <div className="tab-content">
          <h3>Event Booking History</h3>

          {loading ? (
            <p>Loading...</p>
          ) : eventBookingHistory.length > 0 ? (
            <div className="responsive-table">
              <table className="history-table">
                <thead>
                  <tr>
                    <th>Event</th>
                    <th>Venue</th>
                    <th>Date</th>
                    <th>Qty</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th>QR</th>
                  </tr>
                </thead>
                <tbody>
                  {eventBookingHistory.map((booking, idx) => (
                    <tr key={idx}>
                      <td>{booking.event?.heading}</td>
                      <td>{booking.event?.venue}</td>
                      <td>{booking.created_at}</td>
                      <td>{booking.number_of_tickets}</td>
                      <td>{booking.amount_to_pay}</td>
                      <td>
                        <span
                          className={
                            booking.paid ? "status-badge paid" : "status-badge unpaid"
                          }
                        >
                          {booking.paid ? "Paid" : "Unpaid"}
                        </span>
                      </td>
                      <td>
                        {booking.qr_code && (
                          <img
                            src={booking.qr_code}
                            alt="QR"
                            className="qr-small"
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>No event booking history found.</p>
          )}
        </div>
      )}


      <ToastContainer />
    </div>
  );
};

export default ProfilePage;
